import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Info, Loader2, Phone, Upload, X } from "lucide-react";
import { API } from "@/constants";
import TrialezeLogo from "@/components/shared/TrialezeLogo";

// ============ CLIENT INTAKE PAGE (PUBLIC) ============
// Clients access this page with their access code to submit cases

const ClientIntakePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('verify'); // verify, info, interview, documents, review, submitted
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [caseInfo, setCaseInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    case_type: '',
    case_summary: '',
    incident_date: '',
    jurisdiction: '',
    opposing_party: '',
    opposing_party_type: 'individual',
    has_attorney: false,
    prior_attorney: '',
    damages_estimate: '',
    injuries_description: '',
    witnesses: [],
    interview_responses: {}
  });
  const [newWitness, setNewWitness] = useState('');
  
  useEffect(() => {
    if (code) {
      verifyCode();
    }
  }, [code]);
  
  const verifyCode = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/intake/verify/${code}`);
      setCaseInfo(res.data);
      setForm(f => ({
        ...f,
        client_name: res.data.client_name || '',
        client_email: res.data.client_email || '',
        case_type: res.data.case_type || ''
      }));
      // Load questions for this case type
      const qRes = await axios.get(`${API}/intake/questions/${res.data.case_type}`);
      setQuestions(qRes.data.questions || []);
      setStep('info');
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid or expired access code');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };
  
  const addWitness = () => {
    if (!newWitness.trim()) return;
    setForm(f => ({ ...f, witnesses: [...f.witnesses, newWitness.trim()] }));
    setNewWitness('');
  };
  
  const removeWitness = (idx) => {
    setForm(f => ({ ...f, witnesses: f.witnesses.filter((_, i) => i !== idx) }));
  };
  
  const updateInterviewResponse = (id, value) => {
    setForm(f => ({
      ...f,
      interview_responses: { ...f.interview_responses, [id]: value }
    }));
  };
  
  const submitCase = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/intake/submit`, {
        access_code: code,
        ...form
      });
      setStep('submitted');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit case');
    } finally {
      setLoading(false);
    }
  };
  
  const getStepNumber = () => {
    const steps = ['info', 'interview', 'documents', 'review'];
    return steps.indexOf(step) + 1;
  };

  // Error state
  if (step === 'error') {
    return (
      <main className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#1E293B' }}>Access Error</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }
  
  // Submitted state
  if (step === 'submitted') {
    return (
      <main className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#1E293B' }}>Case Submitted!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for submitting your case information. An attorney will review your submission and contact you shortly.
            </p>
            <div className="p-4 bg-slate-50 rounded-lg text-sm text-muted-foreground">
              You may close this page. You will receive updates via email at <strong>{form.client_email}</strong>.
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }
  
  // Loading/Verify state
  if (step === 'verify' || loading) {
    return (
      <main className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-muted-foreground">Verifying access code...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)' }}>
      <div className="container-shell py-8 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <TrialezeLogo size="large" className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#1E293B' }}>Case Intake Form</h1>
          <p className="text-muted-foreground">Please provide information about your case</p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['Basic Info', 'Case Details', 'Documents', 'Review'].map((label, i) => (
            <div key={label} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                getStepNumber() > i + 1 ? 'bg-emerald-500 text-white' :
                getStepNumber() === i + 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {getStepNumber() > i + 1 ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
              </div>
              <span className={`ml-2 text-sm hidden sm:inline ${getStepNumber() === i + 1 ? 'font-semibold text-blue-600' : 'text-muted-foreground'}`}>
                {label}
              </span>
              {i < 3 && <ChevronRight className="h-4 w-4 mx-2 text-slate-300" />}
            </div>
          ))}
        </div>
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Step 1: Basic Info */}
        {step === 'info' && (
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Please confirm your contact information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Full Name *</Label>
                  <Input 
                    value={form.client_name}
                    onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                    placeholder="Your full legal name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={form.client_email}
                    onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input 
                  value={form.client_phone}
                  onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div>
                <Label>Brief Description of Your Case *</Label>
                <Textarea 
                  value={form.case_summary}
                  onChange={e => setForm(f => ({ ...f, case_summary: e.target.value }))}
                  placeholder="Please describe your situation in a few sentences..."
                  rows={4}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Date of Incident</Label>
                  <Input 
                    type="date"
                    value={form.incident_date}
                    onChange={e => setForm(f => ({ ...f, incident_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Location/Jurisdiction</Label>
                  <Input 
                    value={form.jurisdiction}
                    onChange={e => setForm(f => ({ ...f, jurisdiction: e.target.value }))}
                    placeholder="City, State"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setStep('interview')}
                  disabled={!form.client_name || !form.client_email || !form.case_summary}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Continue <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: Interview Questions */}
        {step === 'interview' && (
          <Card>
            <CardHeader>
              <CardTitle>Case Details</CardTitle>
              <CardDescription>Please answer the following questions about your {form.case_type?.replace('_', ' ')} case</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Opposing Party */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-semibold">Opposing Party Information</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Opposing Party Name</Label>
                    <Input 
                      value={form.opposing_party}
                      onChange={e => setForm(f => ({ ...f, opposing_party: e.target.value }))}
                      placeholder="Name of person or company"
                    />
                  </div>
                  <div>
                    <Label>Opposing Party Type</Label>
                    <Select value={form.opposing_party_type} onValueChange={v => setForm(f => ({ ...f, opposing_party_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="corporation">Corporation/Company</SelectItem>
                        <SelectItem value="government">Government Entity</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Dynamic Interview Questions */}
              {questions.map((q, i) => (
                <div key={q.id} className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {q.question}
                    {q.required && <span className="text-red-500">*</span>}
                  </Label>
                  {q.type === 'text' && (
                    <Input 
                      value={form.interview_responses[q.id] || ''}
                      onChange={e => updateInterviewResponse(q.id, e.target.value)}
                      placeholder="Your answer..."
                    />
                  )}
                  {q.type === 'textarea' && (
                    <Textarea 
                      value={form.interview_responses[q.id] || ''}
                      onChange={e => updateInterviewResponse(q.id, e.target.value)}
                      placeholder="Your answer..."
                      rows={3}
                    />
                  )}
                  {q.type === 'date' && (
                    <Input 
                      type="date"
                      value={form.interview_responses[q.id] || ''}
                      onChange={e => updateInterviewResponse(q.id, e.target.value)}
                    />
                  )}
                  {q.type === 'select' && (
                    <Select value={form.interview_responses[q.id] || ''} onValueChange={v => updateInterviewResponse(q.id, v)}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {q.options?.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
              
              {/* Witnesses */}
              <div className="space-y-2">
                <Label>Witnesses (if any)</Label>
                <div className="flex gap-2">
                  <Input 
                    value={newWitness}
                    onChange={e => setNewWitness(e.target.value)}
                    placeholder="Witness name and contact info"
                    onKeyDown={e => e.key === 'Enter' && addWitness()}
                  />
                  <Button type="button" onClick={addWitness} variant="outline">Add</Button>
                </div>
                {form.witnesses.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.witnesses.map((w, i) => (
                      <Badge key={i} variant="secondary" className="gap-1">
                        {w}
                        <button onClick={() => removeWitness(i)} className="ml-1 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Damages */}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Estimated Damages/Losses</Label>
                  <Input 
                    value={form.damages_estimate}
                    onChange={e => setForm(f => ({ ...f, damages_estimate: e.target.value }))}
                    placeholder="e.g., $50,000"
                  />
                </div>
                <div>
                  <Label>Do you have an attorney?</Label>
                  <Select value={form.has_attorney ? 'yes' : 'no'} onValueChange={v => setForm(f => ({ ...f, has_attorney: v === 'yes' }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep('info')}>
                  <ChevronLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={() => setStep('documents')} className="bg-blue-600 hover:bg-blue-700">
                  Continue <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Documents */}
        {step === 'documents' && (
          <Card>
            <CardHeader>
              <CardTitle>Supporting Documents</CardTitle>
              <CardDescription>Upload any relevant documents (optional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: '#D9E4F2' }}>
                <Upload className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-muted-foreground mb-2">Drag and drop files here, or click to browse</p>
                <p className="text-xs text-muted-foreground">Supported: PDF, DOC, DOCX, JPG, PNG (Max 10MB each)</p>
                <Button variant="outline" className="mt-4">
                  <Upload className="mr-2 h-4 w-4" /> Select Files
                </Button>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Helpful Documents to Include:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Photos or videos of the incident</li>
                  <li>• Medical records or bills</li>
                  <li>• Police reports</li>
                  <li>• Correspondence with other parties</li>
                  <li>• Contracts or agreements</li>
                  <li>• Insurance documents</li>
                </ul>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep('interview')}>
                  <ChevronLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={() => setStep('review')} className="bg-blue-600 hover:bg-blue-700">
                  Continue to Review <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 4: Review & Submit */}
        {step === 'review' && (
          <Card>
            <CardHeader>
              <CardTitle>Review Your Submission</CardTitle>
              <CardDescription>Please review your information before submitting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Contact Information</h4>
                  <p className="text-sm"><strong>Name:</strong> {form.client_name}</p>
                  <p className="text-sm"><strong>Email:</strong> {form.client_email}</p>
                  <p className="text-sm"><strong>Phone:</strong> {form.client_phone || 'Not provided'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Case Information</h4>
                  <p className="text-sm"><strong>Type:</strong> {form.case_type?.replace('_', ' ')}</p>
                  <p className="text-sm"><strong>Date:</strong> {form.incident_date || 'Not specified'}</p>
                  <p className="text-sm"><strong>Location:</strong> {form.jurisdiction || 'Not specified'}</p>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2">Case Summary</h4>
                <p className="text-sm">{form.case_summary}</p>
              </div>
              
              {form.opposing_party && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Opposing Party</h4>
                  <p className="text-sm">{form.opposing_party} ({form.opposing_party_type})</p>
                </div>
              )}
              
              {form.damages_estimate && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-emerald-800">Estimated Damages</h4>
                  <p className="text-lg font-bold text-emerald-600">{form.damages_estimate}</p>
                </div>
              )}
              
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  By submitting this form, you consent to an attorney reviewing your case information. This does not create an attorney-client relationship.
                </AlertDescription>
              </Alert>
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep('documents')}>
                  <ChevronLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  onClick={submitCase} 
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                  Submit Case
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
};


export default ClientIntakePage;
